// System Configuration > Product & Feature Configuration -- list view.
// Split 2026-09-09 out of a previously-combined ~1700-line file that held
// this list, the Add Product panel, and the Edit Product panel all in one
// component. Add/Edit now live in their own files (AddProduct.js,
// EditProduct.js), with the pieces identical between those two panels
// factored into shared.js. This file keeps everything that's genuinely
// list-view-only: the product table, its header stat cards, and (as of
// 2026-09-11) the real company-wide feature-navigation totals those cards
// show, read straight off the company-features GET's response wrapper --
// see companyFeatureStats below.
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import AddIcon from "@mui/icons-material/Add";
import AppsIcon from "@mui/icons-material/Apps";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DateRangeIcon from "@mui/icons-material/DateRange";
import ExtensionIcon from "@mui/icons-material/Extension";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ModeStandbyIcon from "@mui/icons-material/ModeStandby";
import TuneIcon from "@mui/icons-material/Tune";
import UpdateIcon from "@mui/icons-material/Update";
import { useSelector } from "react-redux";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { toFlatDisplayHeaders } from "components/CustomComponents/headerGroupUtils";
import { Loader } from "components/Loader";
import { TruncatedTableValue } from "components/DesignSystem/tableColumnSizing";
import {
  GlobalNoticeBanner,
  sharedListCardSx,
  sharedListPageSurfaceSx,
  sharedStickyFooterBarSx,
  sharedTableBodyCellSx,
  sharedTableHeadCellSx,
} from "components/DesignSystem/sharedUiPatterns";
import { ApproveTable } from "pages/TeamZone/Approve/approveUi";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { useFetch } from "services/hooks/useFetch";
import { FilterProvider } from "pages/FilterContext";
import {
  APP_BAR_OFFSET,
  designColors,
  designFontFamily,
  designRadii,
  designShell,
  designType,
  designWeights,
  paginationFooter,
  rowsPerPageOptions,
  StatCard,
  statusChip,
  withDesignFont,
} from "services/constants/designTokens";
import {
  accessStatusLabel,
  daysLeft,
  formatDate,
  isEffectivelyActive,
  mapCompanyProduct,
} from "./shared";
import { AddProduct } from "./AddProduct";
import { EditProduct } from "./EditProduct";
import { withScreenPermissions } from "components/DesignSystem/ScreenAccess";
import { SCREEN_ACCESS } from "services/utils/screenPermissions";

const countChipSx = (color, bg) => ({
  height: 22,
  minWidth: 28,
  fontSize: 11.5,
  fontWeight: designWeights.semibold,
  color,
  bgcolor: bg,
});

// Matches every other list screen's convention (see e.g. userHeader/
// companyHeader in Administration/AdministrationHeaders.js): one entry per
// on-screen column, in the same left-to-right order as the table, each with
// an icon so Filters/Sort/Download render consistently with the rest of the
// app. Grouped shape (the "Features" entry's `children`) matches
// salaryRevisionGroupedHeader (HR/Headers.js) -- headerGroupUtils'
// toFlatDisplayHeaders (used below, feeding CustomFilter's filterHeaders)
// understands this shape natively, flattening each child for the flat
// Filters/Sort/Download field list while the table below still renders the
// {name, children} shape as a real two-row grouped header. No Save View/
// column picker on this screen (removed 2026-09-11 at the user's request,
// after being wired up briefly) -- this array is fixed and always shown in
// full, not a `visibleHeaders` result from a hook.
//
// Split into two groups (rather than one flat array) because the on-screen
// column order is Product, Features, *Utilization*, Access Until, Last
// Updated, Status -- and Utilization isn't a real header at all, just a
// fixed column rendered between these two groups (see the comment above the
// table body's Utilization cell for why: it's still a global stub, same
// value on every row, not a real per-product field yet). The combined
// productListHeaders below (used for Filters/Sort/Download, where
// Utilization doesn't appear anyway) keeps both groups concatenated in this
// same order.
const productListHeadersBeforeUtilization = [
  {
    name: "Product",
    key: "name",
    type: "string",
    icon: <Inventory2Icon fontSize="small" />,
  },
  {
    name: "Features",
    children: [
      {
        name: "Total",
        key: "features_count",
        type: "number",
        icon: <AppsIcon fontSize="small" />,
      },
      {
        name: "Basic",
        key: "basic_features_count",
        type: "number",
        icon: <CheckCircleOutlineIcon fontSize="small" />,
      },
      {
        name: "Add-ons",
        key: "addon_features_count",
        type: "number",
        icon: <ExtensionIcon fontSize="small" />,
      },
    ],
  },
];
const productListHeadersAfterUtilization = [
  {
    name: "Access Until",
    key: "access_until",
    type: "date",
    icon: <DateRangeIcon fontSize="small" />,
  },
  {
    name: "Last Updated",
    key: "updated_at",
    type: "date",
    icon: <UpdateIcon fontSize="small" />,
  },
  {
    name: "Status",
    key: "list_status",
    type: "autocomplete",
    icon: <ModeStandbyIcon fontSize="small" />,
  },
];
const productListHeaders = [
  ...productListHeadersBeforeUtilization,
  ...productListHeadersAfterUtilization,
];
const NotAvailable = ({ children = "Not available" }) => (
  <Mui.Tooltip title="Not tracked by the backend yet">
    <Mui.Typography
      component="span"
      variant="inherit"
      sx={{ color: designColors.slate400 }}
    >
      {children}
    </Mui.Typography>
  </Mui.Tooltip>
);

const AccessUntilCell = ({ item }) => {
  if (!item.access_until) return <span>--</span>;
  const remaining = daysLeft(item.access_until);
  const expired =
    item.access_type !== "Internal" &&
    item.auto_expire !== false &&
    remaining < 0;
  return (
    <Mui.Box>
      <Mui.Typography sx={{ fontSize: 11.5 }}>
        {formatDate(item.access_until)}
      </Mui.Typography>
      {item.access_type !== "Internal" && (
        <Mui.Typography
          sx={{
            fontSize: 10.5,
            fontWeight: 600,
            color: expired ? "#B42318" : designColors.tealDark,
          }}
        >
          {expired ? "Expired" : `${remaining} days left`}
        </Mui.Typography>
      )}
    </Mui.Box>
  );
};

// Table body renders productListHeaders' leaf keys via this switch rather
// than a generic value[key] lookup, since each column needs its own custom
// rendering (chips, AccessUntilCell, statusChip, date formatting). No Save
// View on this screen, but keeping the render-by-key indirection (rather
// than fully hardcoded per-column JSX) still keeps the grouped two-row
// header/body in sync by construction, the same render<Screen>Cell(key, row)
// pattern Anomalies.js/DailyReport.js/PunchDetails.js use.
const renderProductCell = (key, item, isRowSelected) => {
  switch (key) {
    case "name":
      return (
        <Mui.TableCell
          key={key}
          sx={{
            ...sharedTableBodyCellSx,
            fontWeight: isRowSelected
              ? designWeights.heading
              : designWeights.medium,
          }}
        >
          <TruncatedTableValue value={item.name} />
        </Mui.TableCell>
      );
    case "features_count":
      return (
        <Mui.TableCell key={key} align="center" sx={sharedTableBodyCellSx}>
          <Mui.Chip
            size="small"
            label={item.features_count ?? 0}
            sx={countChipSx("#1677FF", "#EEF6FF")}
          />
        </Mui.TableCell>
      );
    case "basic_features_count":
      return (
        <Mui.TableCell key={key} align="center" sx={sharedTableBodyCellSx}>
          <Mui.Chip
            size="small"
            label={item.basic_features_count ?? 0}
            sx={countChipSx(designColors.tealDark, "#E7F8F2")}
          />
        </Mui.TableCell>
      );
    case "addon_features_count":
      return (
        <Mui.TableCell key={key} align="center" sx={sharedTableBodyCellSx}>
          <Mui.Chip
            size="small"
            label={item.addon_features_count ?? 0}
            sx={countChipSx("#EA6A00", "#FFF4E8")}
          />
        </Mui.TableCell>
      );
    case "access_until":
      return (
        <Mui.TableCell key={key} sx={sharedTableBodyCellSx}>
          <AccessUntilCell item={item} />
        </Mui.TableCell>
      );
    case "list_status":
      return (
        <Mui.TableCell key={key} sx={sharedTableBodyCellSx}>
          {statusChip(item.list_status)}
        </Mui.TableCell>
      );
    case "updated_at":
      return (
        <Mui.TableCell
          key={key}
          sx={{ ...sharedTableBodyCellSx, color: designColors.slate600 }}
        >
          {item.updated_at || "--"}
        </Mui.TableCell>
      );
    default:
      return <Mui.TableCell key={key} sx={sharedTableBodyCellSx} />;
  }
};

const STUB_EMPTY_RESULT = [];

const SupportAdminScreen = ({ permissions }) => {
  // Edit permission: add a product and change its features. This screen
  // has no delete action.
  const canEdit = permissions?.canEdit !== false;
  const {
    domain,
    userDetails,
    userScreenAccess,
  } = useSelector((state) => state.tokenReducer);

  // Real provisioning entry confirmed live (2026-09-14): module_name
  // "System Configuration" -> submodule "Products" (not "Product Control",
  // which was only ever a naming *recommendation* left in a prior memory
  // note, never actually implemented here or matched by any real payload).
  // Same `module_details?.find(...)?.submodules?.find(...)?.sub_module_id`
  // pattern every other screen uses (see Administration/index.js's `Users`
  // lookup). Without this, `<FilterProvider>` below was being rendered with
  // no domain/subModuleId/userDetails at all -- CustomFilter's filter/sort
  // save silently posted to `${undefined}filter-preference/`, which the
  // browser resolved as a *relative* URL against the current page
  // (/admin/support/admin), landing on
  // /admin/support/undefinedfilter-preference/ -- exactly the broken
  // request seen live. This only feeds FilterProvider's filter/sort
  // persistence, not a header/column "Save View" picker -- that was
  // deliberately removed from this screen's table on 2026-09-11 (see the
  // comment above productListHeadersBeforeUtilization) and is out of scope
  // here.
  const systemConfigModule = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "System Configuration",
  );
  const subModuleId = systemConfigModule?.submodules?.find(
    (sub) => sub.sub_module_name === "Products",
  )?.sub_module_id;

  // Real GET for the list view (confirmed 2026-09-11): the response is
  // WRAPPED -- { total_products, total_product_count, total_features,
  // basic_features_count, addon_features_count, basic_features_enabled,
  // addon_features_enabled, products: [...] } -- not a bare array as an
  // earlier pass through this file assumed (that assumption happened to
  // still work by accident, since the `.products` fallback below already
  // covered the wrapped shape). The four aggregate fields above are real,
  // company-wide feature-navigation totals -- see companyFeatureStats below,
  // which finally replaces the old permanently-empty modules/moduleResult/
  // featureNavResult stub subsystem the "Total Features"/"Overall
  // Utilization" stat cards used to read (always zero, no backend source).
  // Falls back to the shared STUB_EMPTY_RESULT reference (not a fresh `[]`
  // literal) while no data has loaded yet -- a fresh `[]` would be a new
  // reference every render and re-fire the effect below on every render,
  // not just when real data arrives.
  const {
    result: companyFeaturesResult,
    loading: loadingCompanyFeatures,
    setLoad: reloadCompanyFeatures,
  } = useFetch(domain ? `${domain}product/access/company-features/` : "");
  const productResult = Array.isArray(companyFeaturesResult)
    ? companyFeaturesResult
    : Array.isArray(companyFeaturesResult?.products)
      ? companyFeaturesResult.products
      : STUB_EMPTY_RESULT;
  const loadingProducts = loadingCompanyFeatures;
  const reloadProducts = reloadCompanyFeatures;
  // Company-wide feature-navigation totals the response's wrapper carries
  // alongside `products` -- real numbers now, not per-product (every
  // product's row only lists its own features, with no basic/add-on
  // category tag on each one, so that split can only come from here, not by
  // counting each row's own `features` array).
  const companyFeatureStats = Array.isArray(companyFeaturesResult)
    ? null
    : companyFeaturesResult || null;
  const totalFeatures = companyFeatureStats?.total_features ?? 0;
  const globalBasicFeaturesCount =
    companyFeatureStats?.basic_features_count ?? 0;
  const globalAddonFeaturesCount =
    companyFeatureStats?.addon_features_count ?? 0;
  const totalFeaturesEnabled =
    (companyFeatureStats?.basic_features_enabled ?? 0) +
    (companyFeatureStats?.addon_features_enabled ?? 0);
  const [products, setProducts] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState("");

  // Loaded unconditionally (not gated on a panel being open) -- the list
  // joins these product name/description/feature-count fields onto each
  // company-features row (see mapCompanyProduct), and AddProduct's own
  // Select Product dropdown reuses this same already-fetched list instead
  // of fetching it again.
  const { result: productCatalogResult = [], loading: loadingCatalog } =
    useFetch(domain ? `${domain}product-catalog/` : "");

  // Gates the list view's full-page Loader (see the return below) -- only
  // the two GETs the list itself renders from (company-features + product
  // catalog), not the Add/Edit panels' own per-product fetches, which
  // shouldn't blank out the page behind an already-open panel.
  const isLoading = loadingProducts || loadingCatalog;

  const catalogById = useMemo(
    () =>
      productCatalogResult.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {}),
    [productCatalogResult],
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filteredProducts, setFilteredProducts] = useState(null);
  const filteredProductsSourceRef = useRef(null);
  const [headRow1Node, setHeadRow1Node] = useState(null);
  const [headRow1Height, setHeadRow1Height] = useState(0);
  const headRow1Ref = useCallback((node) => setHeadRow1Node(node), []);
  useEffect(() => {
    if (!headRow1Node) return;
    const update = () => setHeadRow1Height(headRow1Node.offsetHeight);
    update();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(headRow1Node);
    return () => observer.disconnect();
  }, [headRow1Node]);
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  useEffect(() => {
    if (!customFilterNode) return;
    const update = () => setCustomFilterHeight(customFilterNode.offsetHeight);
    update();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode]);
  // mapCompanyProduct first (raw rows use product_id/product_name/end_date,
  // not this file's own id/name/access_until), *then* filter on the mapped
  // `name` -- filtering on the raw item's (nonexistent) `.name` before
  // mapping would never actually match "Support Admin", silently letting it
  // through the list.
  useEffect(() => {
    if (!loadingProducts) {
      const next = (Array.isArray(productResult) ? productResult : [])
        .map((item) => mapCompanyProduct(item, catalogById))
        .filter((item) => item.name !== "Support Admin");
      setProducts(next);
    }
  }, [productResult, loadingProducts, catalogById]);
  const activeProducts = products.filter(isEffectivelyActive).length;
  const expiredProducts = products.filter(
    (item) => accessStatusLabel(item) === "Expired",
  ).length;

  const editingProduct = editingProductId
    ? products.find((item) => item.id === editingProductId) || null
    : null;

  const productRows = useMemo(
    () =>
      products.map((item) => ({
        ...item,
        list_status: accessStatusLabel(item),
      })),
    [products],
  );
  const filteredProductsReady =
    filteredProductsSourceRef.current === productRows;
  const visibleProducts =
    filteredProducts !== null && filteredProductsReady
      ? filteredProducts
      : productRows;
  const handleFilteredProductsChange = useCallback(
    (items) => {
      filteredProductsSourceRef.current = productRows;
      setFilteredProducts(items);
      if (page * rowsPerPage >= items.length) setPage(0);
    },
    [page, productRows, rowsPerPage],
  );
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };
  useEffect(() => {
    if (page * rowsPerPage >= visibleProducts.length && page > 0) setPage(0);
  }, [page, rowsPerPage, visibleProducts.length]);

  // productListHeaders is a fixed, always-fully-visible column set here --
  // no Save View/column picker on this screen (removed 2026-09-11 at the
  // user's request). visibleLeafColumnCount still counts real leaf columns
  // (the Features group's 3 children count as 3, not 1) so the empty-state
  // row's colSpan stays correct if the header array ever changes shape.
  const visibleLeafColumnCount = productListHeaders.reduce(
    (total, header) => total + (header.children ? header.children.length : 1),
    0,
  );

  // No "Company Name" line: product configuration is schema-wide and the
  // top bar's company selector is hidden on this screen.
  const reportMetadata = useMemo(
    () => ({
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [],
  );

  const refresh = () => {
    reloadProducts(true);
  };

  // Whole-page swap to the shared Loader while the list's own data is in
  // flight, same pattern Administration/ListView.js (Users) uses -- not a
  // small inline spinner, the full page content is replaced until ready.
  if (isLoading) {
    return <Loader info="Loading..." />;
  }
  return (
    <Mui.ThemeProvider theme={withDesignFont}>
      <FilterProvider
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        <Mui.Box
          sx={{ ...sharedListPageSurfaceSx, fontFamily: designFontFamily }}
        >
          <Mui.Stack spacing={2.25}>
            <Mui.Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={{ xs: 1.5, sm: 0 }}
            >
              <Mui.Box>
                <Mui.Typography sx={designType.pageTitle}>
                  Product &amp; Feature Configuration
                </Mui.Typography>
                <Mui.Typography sx={designType.pageSubtitle}>
                  Manage products/modules and the features enabled for your
                  organization.
                </Mui.Typography>
              </Mui.Box>
              {canEdit && (
                <PrimaryButton
                  onClick={() => setAddOpen(true)}
                  startIcon={<AddIcon />}
                >
                  Add Product
                </PrimaryButton>
              )}
            </Mui.Stack>
            <Mui.Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  sm: "repeat(2, minmax(0, 1fr))",
                  xl: "repeat(3, minmax(0, 1fr))",
                },
                "@media (max-width:599.95px)": {
                  "& > :last-child:nth-child(odd)": { gridColumn: "1 / -1" },
                },
                gap: 1.5,
              }}
            >
              <StatCard
                icon={Inventory2Icon}
                title="Total Products"
                value={products.length}
                detail={`${activeProducts} Active   ${expiredProducts} Expired`}
                accent={designColors.tealDark}
                tint="#E7F8F2"
              />
              <StatCard
                icon={AppsIcon}
                title="Total Features"
                value={totalFeatures}
                detail={`${globalBasicFeaturesCount} Basic   ${globalAddonFeaturesCount} Add-ons`}
                accent="#1677FF"
                tint="#EEF6FF"
              />
              <StatCard
                icon={TuneIcon}
                title="Overall Utilization"
                value={
                  totalFeatures
                    ? `${Math.round((totalFeaturesEnabled / totalFeatures) * 100)}%`
                    : "--"
                }
                detail={
                  totalFeatures ? (
                    `${totalFeaturesEnabled} of ${totalFeatures} features enabled`
                  ) : (
                    <NotAvailable>Feature data unavailable</NotAvailable>
                  )
                }
                accent="#EA6A00"
                tint="#FFF4E8"
              />
            </Mui.Box>
            <Mui.Box>
              <Mui.Box
                ref={customFilterRef}
                sx={{
                  position: "sticky",
                  top: APP_BAR_OFFSET,
                  zIndex: 4,
                  backgroundColor: designColors.white,
                  borderTopLeftRadius: designRadii.card,
                  borderTopRightRadius: designRadii.card,
                }}
              >
                <CustomFilter
                  title="Products"
                  filterHeaders={toFlatDisplayHeaders(productListHeaders)}
                  data={productRows}
                  filteredData={visibleProducts}
                  filteredDataReady={
                    filteredProducts !== null && filteredProductsReady
                  }
                  onFilteredDataChange={handleFilteredProductsChange}
                  screenAccess
                  hideCount
                  downloadFormats={["excel", "pdf", "csv"]}
                  includeMetadata
                  metadata={reportMetadata}
                  labeledToolbar
                  searchPlaceholder="Search products..."
                />
              </Mui.Box>
              <Mui.Paper
                elevation={0}
                sx={{ ...sharedListCardSx, p: 0, mt: 0, borderRadius: 0 }}
              >
                <Mui.TableContainer
                  sx={{
                    // No maxHeight/overflow:"auto" -- matches Users
                    // (Administration/ListView.js) and the Approve
                    // sub-screens: the page scrolls, not a bounded box
                    // inside it, so the sticky head (pinned under
                    // AppBar+CustomFilter above) never fights its own
                    // separate scrollport for what "top" means.
                    overflowX: "visible",
                    p: 1,
                  }}
                >
                  <ApproveTable stickyHeader sx={{ minWidth: 1180 }}>
                    <Mui.TableHead sx={designShell.tableStickyHead}>
                      <Mui.TableRow
                        ref={headRow1Ref}
                        sx={designShell.tableStickyHeadRow}
                      >
                        {productListHeadersBeforeUtilization.map((header) => (
                          <Mui.TableCell
                            key={header.key || header.name}
                            align={header.children ? "center" : undefined}
                            colSpan={
                              header.children ? header.children.length : 1
                            }
                            rowSpan={header.children ? 1 : 2}
                            sx={{
                              ...sharedTableHeadCellSx,
                              top: APP_BAR_OFFSET + customFilterHeight,
                            }}
                          >
                            <TruncatedTableValue value={header.name} />
                          </Mui.TableCell>
                        ))}
                        {/* Utilization is a fixed column between the two
                            productListHeaders groups, not part of either --
                            see the comment above that array for why. */}
                        <Mui.TableCell
                          rowSpan={2}
                          sx={{
                            ...sharedTableHeadCellSx,
                            top: APP_BAR_OFFSET + customFilterHeight,
                          }}
                        >
                          <TruncatedTableValue value="Utilization" />
                        </Mui.TableCell>
                        {productListHeadersAfterUtilization.map((header) => (
                          <Mui.TableCell
                            key={header.key || header.name}
                            rowSpan={2}
                            sx={{
                              ...sharedTableHeadCellSx,
                              top: APP_BAR_OFFSET + customFilterHeight,
                            }}
                          >
                            <TruncatedTableValue value={header.name} />
                          </Mui.TableCell>
                        ))}
                      </Mui.TableRow>
                      <Mui.TableRow>
                        {productListHeaders
                          .filter((header) => header.children)
                          .flatMap((header) =>
                            header.children.map((child) => (
                              <Mui.TableCell
                                key={child.key}
                                align="center"
                                sx={{
                                  ...sharedTableHeadCellSx,
                                  top:
                                    APP_BAR_OFFSET +
                                    customFilterHeight +
                                    headRow1Height,
                                }}
                              >
                                <TruncatedTableValue value={child.name} />
                              </Mui.TableCell>
                            )),
                          )}
                      </Mui.TableRow>
                    </Mui.TableHead>
                    <Mui.TableBody>
                      {visibleProducts
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage,
                        )
                        .map((item) => {
                          const isRowSelected = item.id === editingProductId;
                          return (
                            <Mui.TableRow
                              key={item.id}
                              hover
                              onClick={() => setEditingProductId(item.id)}
                              sx={{
                                cursor: "pointer",
                                ...designShell.tableRowHover,
                                ...(isRowSelected
                                  ? designShell.neutralSelectedRow
                                  : {}),
                              }}
                            >
                              {/* Per-product counts from the company-features
                                  GET response, joined with the product
                                  catalog (see mapCompanyProduct) -- not
                                  totalFeatures/totalFeaturesEnabled, the
                                  *global* company-wide totals the stat cards
                                  above and the Utilization cell below use,
                                  which are the same number on every row
                                  regardless of product. */}
                              {productListHeadersBeforeUtilization.flatMap(
                                (header) =>
                                  header.children
                                    ? header.children.map((child) =>
                                        renderProductCell(
                                          child.key,
                                          item,
                                          isRowSelected,
                                        ),
                                      )
                                    : [
                                        renderProductCell(
                                          header.key,
                                          item,
                                          isRowSelected,
                                        ),
                                      ],
                              )}
                              <Mui.TableCell sx={sharedTableBodyCellSx}>
                                <Mui.Typography sx={{ fontSize: 11 }}>
                                  {totalFeatures ? (
                                    `${Math.round((totalFeaturesEnabled / totalFeatures) * 100)}%`
                                  ) : (
                                    <NotAvailable>--</NotAvailable>
                                  )}
                                </Mui.Typography>
                                <Mui.LinearProgress
                                  variant="determinate"
                                  value={
                                    (totalFeaturesEnabled /
                                      Math.max(totalFeatures, 1)) *
                                    100
                                  }
                                  sx={{
                                    mt: 0.5,
                                    height: 5,
                                    borderRadius: 2,
                                    bgcolor: designColors.slate100,
                                    "& .MuiLinearProgress-bar": {
                                      bgcolor: designColors.tealDark,
                                    },
                                  }}
                                />
                              </Mui.TableCell>
                              {productListHeadersAfterUtilization.map(
                                (header) =>
                                  renderProductCell(
                                    header.key,
                                    item,
                                    isRowSelected,
                                  ),
                              )}
                            </Mui.TableRow>
                          );
                        })}
                      {!loadingProducts && !visibleProducts.length && (
                        <TableEmptyState
                          dataLength={productRows}
                          colSpan={visibleLeafColumnCount + 1}
                        />
                      )}
                    </Mui.TableBody>
                  </ApproveTable>
                </Mui.TableContainer>
                {visibleProducts.length > rowsPerPage && (
                  <Mui.Box
                    sx={{ ...sharedStickyFooterBarSx, px: 1.5, py: 1.3 }}
                  >
                    {paginationFooter({
                      page,
                      rowsPerPage,
                      total: visibleProducts.length,
                      itemLabel: "products",
                      onPageChange: setPage,
                      onRowsPerPageChange: handleRowsPerPageChange,
                      rowsPerPageOptions,
                    })}
                  </Mui.Box>
                )}
              </Mui.Paper>
            </Mui.Box>
            <GlobalNoticeBanner icon={<InfoOutlinedIcon fontSize="small" />}>
              <strong>About Product &amp; Feature Configuration</strong>
              <br />
              Enable the required products/modules and select the features your
              organization needs. Licensing is allocated based on active
              employees.
            </GlobalNoticeBanner>
          </Mui.Stack>
          <AddProduct
            open={canEdit && addOpen}
            onClose={() => setAddOpen(false)}
            productCatalog={productCatalogResult}
            configuredProductIds={(Array.isArray(productResult) ? productResult : []).map(
              (item) => item?.product_id,
            )}
            onRefresh={refresh}
          />
          <EditProduct
            open={Boolean(editingProductId)}
            product={editingProduct}
            catalogEntry={
              editingProduct ? catalogById[editingProduct.id] : null
            }
            onClose={() => setEditingProductId("")}
            onRefresh={refresh}
            canEdit={canEdit}
          />
        </Mui.Box>
      </FilterProvider>
    </Mui.ThemeProvider>
  );
};

export const SupportAdmin = withScreenPermissions(
  SupportAdminScreen,
  SCREEN_ACCESS.productControl,
  "Product & Feature Configuration",
);
